<template>
  <a-layout style="height: 100%;background-color: #fff;">
    <!-- 左侧目录 -->
    <a-layout-sider width="410" style="background: #fff; overflow: auto; position: fixed; height: 85%;">
      <a-menu
        style="height: 100%;"
        mode="inline"
        :selectedKeys="[currentKey]"
        @click="handleMenuClick"
      >
        <a-menu-item
          v-for="item in toc"
          :key="item.key"
          :style="{ paddingLeft: `${(item.level - 1) * 24}px`, fontSize: `16px` }"
        >
          <a @click.prevent="scrollToAnchor(item.anchor)">{{ item.text }}</a>
        </a-menu-item>
      </a-menu>
    </a-layout-sider>
    <!-- 右侧内容 -->
    <a-layout-content style="margin-left: 410px; padding: 24px; overflow: auto; background-color: #fff;">
      <div class="markdown-body">
        <div v-for="(content, index) in renderedContentSlices" :key="index">
          <template v-if="content.type === 'html'">
            <div v-html="content.data"></div>
          </template>
          <template v-else-if="content.type === 'component'">
            <BannerCarousel :pics="content.params" style="margin: 20px;"/>
          </template>
        </div>
      </div>
    </a-layout-content>
  </a-layout>
</template>

<script>
import MarkdownIt from 'markdown-it'
import hljs from 'highlight.js'
import 'highlight.js/styles/github.css'
import 'github-markdown-css/github-markdown.css'
import BannerCarousel from '@/components/BannerCarousel.vue';

export default {
  name: 'MarkdownViewer',
  components: {
    BannerCarousel,
  },
  props: {
    markdownContent: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      htmlContent: '',
      toc: [],
      currentKey: '0',
    }
  },
  computed: {
    renderedContentSlices() {
      const slices = [];
      let remainingHTML = this.htmlContent;

      // 使用正则表达式查找占位符并提取参数
      const regex = /\[\[carousel\|([^\]]+)\]\]/;

      while (remainingHTML) {
        const match = regex.exec(remainingHTML);
        if (!match) {
          slices.push({ type: 'html', data: remainingHTML });
          break;
        }

        const pos = match.index;
        if (pos > 0) {
          slices.push({ type: 'html', data: remainingHTML.slice(0, pos) });
        }

        slices.push({ type: 'component', params: match[1] });

        remainingHTML = remainingHTML.slice(pos + match[0].length);
      }

      return slices;
    },
  },
  methods: {
    renderMarkdown() {
      const md = new MarkdownIt({
        html: true,
        linkify: false,
        highlight: (str, lang) => {
          if (lang && hljs.getLanguage(lang)) {
            try {
              return '<pre class="hljs"><code>' +
                hljs.highlight(lang, str, true).value +
                '</code></pre>'
            } catch (err) {
              console.log(err)
            }
          }
          return '<pre class="hljs"><code>' + md.utils.escapeHtml(str) + '</code></pre>'
        },
      })

      // 重写渲染规则，提取标题生成目录
      const defaultRender = md.renderer.rules.heading_open || function(tokens, idx, options, env, self) {
        return self.renderToken(tokens, idx, options)
      }

      md.renderer.rules.heading_open = (tokens, idx, options, env, self) => {
        const token = tokens[idx]
        const level = Number(token.tag.substr(1))
        const title = tokens[idx + 1].content
        const anchor = 'heading-' + idx
        this.toc.push({
          level: level,
          text: title,
          anchor: anchor,
          key: idx.toString(),
        })
        token.attrs = token.attrs || []
        token.attrs.push(['id', anchor])
        return defaultRender(tokens, idx, options, env, self)
      }

      this.htmlContent = md.render(this.markdownContent)
    },
    handleMenuClick(e) {
      this.currentKey = e.key
    },
    scrollToAnchor(anchor) {
      const element = document.getElementById(anchor)
      if (element) {
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - 100
        window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
        })
      }
    },
    updateCurrentSection() {
      let closestSectionKey = null;
      let smallestDifference = Infinity;
      
      this.toc.forEach((item) => {
        const element = document.getElementById(item.anchor);
        if (!element) return; // 如果元素不存在，跳过

        const rect = element.getBoundingClientRect();

        const difference = Math.abs(rect.top - 110);

        if (difference < smallestDifference) {
          smallestDifference = difference;
          closestSectionKey = item.key;
        }
      });

      // 更新当前选中的目录项
      if (closestSectionKey && closestSectionKey !== this.currentKey) {
        this.currentKey = closestSectionKey;
      }
    },
  },
  mounted() {
    this.renderMarkdown()
    window.addEventListener('scroll', this.updateCurrentSection)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.updateCurrentSection)
  },
  watch: {
    markdownContent() {
      // 当输入的 markdownContent 发生变化时，重新渲染
      this.toc = []
      this.renderMarkdown()
    },
  },
}
</script>

<style scoped>
.markdown-body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue',
    Arial, 'Noto Sans', 'Liberation Sans', sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
  font-size: 17px;
  line-height: 1.6;
  color: #24292e;
  background-color: #fff;
  text-align: justify;
  line-height: 2;
}
.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
  margin-top: 1em;
  margin-bottom: 16px;
  font-weight: bold;
}
.markdown-body p {
  margin: 0 0 10px;
}
.markdown-body pre {
  background-color: #f6f8fa;
  padding: 16px;
  overflow: auto;
  border-radius: 3px;
}
.markdown-body code {
  background-color: rgba(27, 31, 35, 0.05);
  padding: 2px 4px;
  font-size: 85%;
  border-radius: 3px;
}
</style>

<style>
.markdown-body p {
  margin-bottom: 0px;
}
.markdown-body h2 {
  margin:10px 0 5px 0;
  padding: 10px 30px; /* 内边距，左右30px扩展背景 */
  display: inline-block; /* 使背景适应文本宽度 */
  background-image: linear-gradient(to right, #b6e5ff97, #e6f7ff5e); /* 指定从深色到浅色的背景渐变 */
  white-space: nowrap; /* 防止在移动屏幕尺寸时折行 */
  border-bottom: 0;
  border-left: 3px solid rgba(0, 0, 0, 0.429);
}
</style>
